import React, { useEffect, useState } from "react";
import api, { formatApiError, resolveMedia } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, X, Pencil, DatabaseBackup } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

const CATEGORIES = ["Bauherr", "Planung", "Förderer", "Sonstige"];

export default function AdminPartners() {
  const [page, setPage] = useState({ heading: "", intro: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // partner being edited or { __new: true }
  const [savingPage, setSavingPage] = useState(false);
  const [seeding, setSeeding] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [pg, ps] = await Promise.all([api.get("/partners-page"), api.get("/partners")]);
      setPage({ heading: pg.data.heading || "", intro: pg.data.intro || "" });
      setItems(ps.data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function savePage() {
    setSavingPage(true);
    try { await api.put("/partners-page", page); toast.success("Seitentexte gespeichert"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPage(false); }
  }

  async function seedPartners() {
    if (!window.confirm("Die offizielle Partner-Liste (LIST → GEFA Fabritz, 17 Firmen) wird in diese Umgebung importiert. Bestehende Partner mit gleichem Namen werden aktualisiert (eigene Logos bleiben erhalten). Fortfahren?")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-partners");
      toast.success(`${data.inserted} neu importiert · ${data.updated} aktualisiert (gesamt ${data.total})`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import fehlgeschlagen");
    } finally { setSeeding(false); }
  }

  async function savePartner(p) {
    try {
      if (p.__new) {
        await api.post("/partners", p);
        toast.success("Partner angelegt");
      } else {
        await api.put(`/partners/${p.id}`, p);
        toast.success("Gespeichert");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function del(id) {
    if (!window.confirm("Partner wirklich löschen?")) return;
    try { await api.delete(`/partners/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div data-testid="admin-partners">
      <h1 className="font-semibold text-3xl mb-1">Partner</h1>
      <p className="text-sm text-neutral-500 mb-8">Seitentexte und Partner-Übersicht pflegen.</p>

      <section className="bg-white border border-brand-line p-6 mb-10">
        <h2 className="font-semibold text-lg mb-4">Seitentexte</h2>
        <label className="block mb-3">
          <span className="block text-xs text-neutral-500 mb-1.5">Überschrift</span>
          <input value={page.heading} onChange={(e) => setPage({ ...page, heading: e.target.value })} data-testid="partners-page-heading" className="w-full border border-brand-line px-3 py-2.5 text-sm" />
        </label>
        <label className="block mb-4">
          <span className="block text-xs text-neutral-500 mb-1.5">Einleitung</span>
          <RichTextEditor
            value={page.intro}
            onChange={(html) => setPage({ ...page, intro: html })}
            minHeight={120}
            placeholder="Einleitung der Partner-Seite…"
            testid="partners-page-intro"
          />
        </label>
        <button onClick={savePage} disabled={savingPage} data-testid="partners-page-save" className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-5 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))] disabled:opacity-50">
          <Save className="h-4 w-4" /> {savingPage ? "Speichern…" : "Speichern"}
        </button>
      </section>

      <section>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-semibold text-lg">Partner-Übersicht</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={seedPartners}
              disabled={seeding}
              data-testid="partners-seed"
              title="Importiert die offizielle Partner-Liste (LIST → GEFA Fabritz). Idempotent — vorhandene Partner werden aktualisiert."
              className="inline-flex items-center gap-2 border border-brand-line bg-white px-4 py-2.5 text-sm hover:bg-neutral-50 disabled:opacity-50"
            >
              <DatabaseBackup className="h-4 w-4" /> {seeding ? "Importiere…" : "Offizielle Liste importieren"}
            </button>
            <button onClick={() => setEditing({ __new: true, name: "", description: "", logo_url: "", website: "", category: "Sonstige", order: items.length, active: true, role: "", contact_name: "", contact_email: "", contact_phone: "" })} data-testid="partner-new" className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-5 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))]">
              <Plus className="h-4 w-4" /> Neuer Partner
            </button>
          </div>
        </div>

        <div className="bg-white border border-brand-line">
          {loading ? <p className="p-8 text-sm text-neutral-500">Lade…</p> :
          items.length === 0 ? <p className="p-8 text-sm text-neutral-500">Noch keine Partner angelegt.</p> : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-5 py-3 font-normal">Logo</th>
                  <th className="text-left px-5 py-3 font-normal">Name</th>
                  <th className="text-left px-5 py-3 font-normal">Kategorie</th>
                  <th className="text-left px-5 py-3 font-normal">Sortierung</th>
                  <th className="text-left px-5 py-3 font-normal">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-brand-line" data-testid={`partner-row-${p.id}`}>
                    <td className="px-5 py-3"><div className="h-10 w-16 bg-neutral-100 flex items-center justify-center">{p.logo_url ? <img src={resolveMedia(p.logo_url)} alt="" className="max-h-10 max-w-16 object-contain" /> : <span className="text-neutral-300 text-xs">—</span>}</div></td>
                    <td className="px-5 py-3 font-medium">{p.name}</td>
                    <td className="px-5 py-3"><span className="inline-block px-2 py-0.5 text-xs border border-brand-line text-neutral-600">{p.category}</span></td>
                    <td className="px-5 py-3 text-neutral-500">{p.order}</td>
                    <td className="px-5 py-3"><span className={`text-xs ${p.active ? "text-[hsl(var(--brand-green-strong))]" : "text-neutral-400"}`}>{p.active ? "Aktiv" : "Inaktiv"}</span></td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => setEditing(p)} className="text-neutral-500 hover:text-[hsl(var(--brand-green-strong))]" data-testid={`edit-partner-${p.id}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => del(p.id)} className="text-neutral-500 hover:text-red-600" data-testid={`delete-partner-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {editing && <PartnerEditor partner={editing} onClose={() => setEditing(null)} onSave={savePartner} />}
    </div>
  );
}

function PartnerEditor({ partner, onClose, onSave }) {
  const [p, setP] = useState(partner);
  const [busy, setBusy] = useState(false);

  async function uploadLogo(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((s) => ({ ...s, logo_url: data.url }));
      toast.success("Logo hochgeladen");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="partner-editor">
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-semibold text-2xl">{partner.__new ? "Neuer Partner" : "Partner bearbeiten"}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900 text-2xl"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="block text-xs text-neutral-500 mb-1.5">Name *</span>
            <input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="partner-name" required className="w-full border border-brand-line px-3 py-2.5 text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs text-neutral-500 mb-1.5">Logo</span>
            {p.logo_url && <img src={resolveMedia(p.logo_url)} alt="" className="h-16 mb-2 object-contain" />}
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 border border-brand-line px-3 py-2 text-xs cursor-pointer hover:bg-neutral-50">
                <Upload className="h-3.5 w-3.5" /> {p.logo_url ? "Ersetzen" : "Hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={busy} />
              </label>
              {p.logo_url && <button type="button" onClick={() => setP({ ...p, logo_url: "" })} className="text-xs text-neutral-500 hover:text-red-600">Entfernen</button>}
              <input value={p.logo_url || ""} onChange={(e) => setP({ ...p, logo_url: e.target.value })} placeholder="oder URL" className="flex-1 border border-brand-line px-3 py-2 text-sm" />
            </div>
          </label>
          <label className="block">
            <span className="block text-xs text-neutral-500 mb-1.5">Beschreibung</span>
            <RichTextEditor
              value={p.description}
              onChange={(html) => setP({ ...p, description: html })}
              minHeight={120}
              placeholder="Kurzbeschreibung des Partners…"
              testid="partner-description"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-neutral-500 mb-1.5">Website (optional)</span>
            <input value={p.website || ""} onChange={(e) => setP({ ...p, website: e.target.value })} placeholder="https://" data-testid="partner-website" className="w-full border border-brand-line px-3 py-2.5 text-sm" />
          </label>
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Kategorie</span>
              <select value={p.category} onChange={(e) => setP({ ...p, category: e.target.value })} data-testid="partner-category" className="w-full border border-brand-line px-3 py-2.5 text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Sortierung</span>
              <input type="number" value={p.order} onChange={(e) => setP({ ...p, order: parseInt(e.target.value) || 0 })} className="w-full border border-brand-line px-3 py-2.5 text-sm" />
            </label>
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Status</span>
              <label className="inline-flex items-center gap-2 mt-2 text-sm">
                <input type="checkbox" checked={p.active} onChange={(e) => setP({ ...p, active: e.target.checked })} /> Aktiv
              </label>
            </label>
          </div>

          {/* Projekt-Rolle + Ansprechpartner */}
          <label className="block">
            <span className="block text-xs text-neutral-500 mb-1.5">Rolle im Projekt (z. B. „Schallgutachter", „Architektur")</span>
            <input value={p.role || ""} onChange={(e) => setP({ ...p, role: e.target.value })} data-testid="partner-role" placeholder="Optionale Projektrolle" className="w-full border border-brand-line px-3 py-2.5 text-sm" />
          </label>
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Ansprechpartner</span>
              <input value={p.contact_name || ""} onChange={(e) => setP({ ...p, contact_name: e.target.value })} data-testid="partner-contact-name" placeholder="Vor- und Nachname" className="w-full border border-brand-line px-3 py-2.5 text-sm" />
            </label>
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">E-Mail</span>
              <input type="email" value={p.contact_email || ""} onChange={(e) => setP({ ...p, contact_email: e.target.value })} data-testid="partner-contact-email" placeholder="name@firma.de" className="w-full border border-brand-line px-3 py-2.5 text-sm" />
            </label>
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Telefon</span>
              <input value={p.contact_phone || ""} onChange={(e) => setP({ ...p, contact_phone: e.target.value })} data-testid="partner-contact-phone" placeholder="+49 …" className="w-full border border-brand-line px-3 py-2.5 text-sm" />
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="border border-brand-line px-5 py-2.5 text-sm hover:bg-neutral-50">Abbrechen</button>
          <button onClick={() => onSave(p)} data-testid="partner-save" className="bg-[hsl(var(--brand-green-dark))] text-white px-6 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))]">Speichern</button>
        </div>
      </div>
    </div>
  );
}
